import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
} from '@angular/core';
import { CorporateDesignStore } from '../services/api/corporate-design/corporate-design-store.service';
import { DarkModeStore } from '../services/api/dark-mode/dark-mode-store.service';
import { resolveTheme, ThemeDesign } from './resolve-theme';
import { themeVariables } from './theme-variables';
import { ThemeContext, ThemeMode, ThemeModePreference } from './theme.types';

@Component({
  selector: 'aw-theme',
  template: '<ng-content />',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
      color: var(--color-standard-text-primary);
      background-color: var(--color-standard-background);
    }
  `,
  host: {
    '[attr.data-aw-context]': 'context()',
    '[attr.data-aw-mode]': 'resolvedMode()',
    '[style]': 'cssVariables()',
  },
})
export class ThemeComponent {
  // Context is explicit so a nested shop never depends on the CMS route state.
  readonly context = input.required<ThemeContext>();
  /** Auto follows the existing saved/system preference, in either context. */
  readonly mode = input<ThemeModePreference>('auto');
  /** Omit to use the loaded shop design; pass a draft for an isolated preview. */
  readonly design = input<ThemeDesign | null>();

  private readonly injector = inject(Injector);

  readonly resolvedMode = computed<ThemeMode>(() => {
    const mode = this.mode();
    return mode === 'auto'
      ? this.injector.get(DarkModeStore).isActive()
        ? 'dark'
        : 'light'
      : mode;
  });

  readonly tokens = computed(() => {
    const context = this.context();
    const design =
      context === 'shop'
        ? (this.design() ??
          this.injector.get(CorporateDesignStore).corporateDesign())
        : undefined;
    return resolveTheme(context, this.resolvedMode(), design);
  });

  protected readonly cssVariables = computed(() => {
    return {
      ...themeVariables(this.tokens(), this.context(), this.resolvedMode()),
      'color-scheme': this.resolvedMode(),
    };
  });
}
